import React, { FC, ReactNode, useEffect } from "react";
import { AlertMessageType, newAlertMessage } from "../alert-message";
import { showAutoClosingAlert } from "../../alerts/alerts";
import {
  ActionTypes,
  ApplicationError,
  GenericCUD,
  NetworkActionHumanReadable_HumanReadableValue,
  NetworkActionHumanReadable_NetworkName,
  NetworkActionToHumanReadable,
  VPDNetworkStatusAction,
} from "../../data-types";
import { Kind } from "graphql";

type Props = {
  operation: any;
};

export const GlobalUserAlert: FC<Props> = ({ operation }) => {
  useEffect(() => {
    showAlert(operation);
  }, [operation]);

  return <></>;
};

const showAlert = (operation: VPDNetworkStatusAction): void => {
  if (networkStatusActionIsCUDAndNotRequest(operation)) {
    switch (operation.type) {
      case ActionTypes.SUCCESS:
        return showAutoClosingAlert(
          maybeShowAlertMessage("success", operation, ActionTypes.SUCCESS)
        );
      case ActionTypes.ERROR:
      case ActionTypes.CANCEL:
        return showAutoClosingAlert(maybeShowAlertMessage("error", operation, ActionTypes.ERROR));
      default:
        return showAutoClosingAlert(maybeShowAlertMessage("info", operation, ActionTypes.SUCCESS));
    }
  }
};

const maybeShowAlertMessage = (
  type: AlertMessageType,
  operation: VPDNetworkStatusAction,
  actionType: ActionTypes,
  description?: ReactNode,
  cause?: ApplicationError,
  onClose?: () => void
) => {
  const networkActionName = getNetworkActionName(operation, actionType);

  if (
    operation.payload.operation?.operationName === "updateQuickUpdateShownAt" ||
    operation.payload.operation?.operationName === "BedrockCreateAllEmbeddings" ||
    operation.payload.operation?.operationName === "updateLastLoginAt"
  )
    return;

  if (networkActionName === "Unknown") return;

  return newAlertMessage(type, networkActionName, description, cause, onClose);
};

const getNetworkActionName = (
  operation: VPDNetworkStatusAction,
  operationType: ActionTypes = ActionTypes.SUCCESS
): NetworkActionHumanReadable_HumanReadableValue | GenericCUD => {
  const networkActionName = getNetworkActionNameFromOperation(operation);
  const humanReadableNetworkActionName = getHumanReadableNetworkActionName(
    networkActionName,
    operationType
  );
  return humanReadableNetworkActionName;
};

const getNetworkActionNameFromOperation = (operation: VPDNetworkStatusAction): string => {
  const { payload } = operation;
  const { operation: innerOperation } = payload;

  if (innerOperation) {
    const { operationName } = innerOperation;
    return operationName;
  }

  return "";
};

const getHumanReadableNetworkActionName = (
  action: NetworkActionHumanReadable_NetworkName | string,
  actionType: ActionTypes
): NetworkActionHumanReadable_HumanReadableValue | GenericCUD => {
  const humanReadableNetworkAction = Object.prototype.hasOwnProperty.call(
    NetworkActionToHumanReadable,
    action
  )
    ? NetworkActionToHumanReadable[
        `${action}${
          actionType !== ActionTypes.SUCCESS ? "_Failed" : ""
        }` as NetworkActionHumanReadable_NetworkName
      ]
    : undefined;

  if (!humanReadableNetworkAction) {
    return getGenericNetworkAction(action);
  }

  return humanReadableNetworkAction;
};

const getGenericNetworkAction = (action: string): GenericCUD => {
  if (action.toLowerCase().includes("create")) {
    return GenericCUD.CREATED;
  } else if (action.toLowerCase().includes("update")) {
    return GenericCUD.UPDATED;
  } else if (action.toLowerCase().includes("delete")) {
    return GenericCUD.DELETED;
  } else if (action.toLowerCase().includes("copy") || action.toLowerCase().includes("copied")) {
    return GenericCUD.COPIED;
  }
  return GenericCUD.UNKNOWN;
};

const networkStatusActionIsCUDAndNotRequest = (
  networkOperation: VPDNetworkStatusAction
): boolean => {
  const {
    payload: { operation },
  } = networkOperation;

  if (operation) {
    const notRequest = networkOperation.type !== ActionTypes.REQUEST;
    const {
      query: { definitions = [] },
    } = operation;

    if (
      notRequest &&
      definitions.length > 0 &&
      definitions[0].kind === Kind.OPERATION_DEFINITION &&
      definitions[0].operation === "mutation"
    ) {
      return true;
    }
  }

  return false;
};
